import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { BulkEditSectionFormService, MultiSelect, type DropdownOption } from '@zag/ui';
import type { ManagedPermission } from '@zag/auth/models';

const SECTION_ID = 'bulk-permissions';

interface State {
  permissionIds: string[];
}

@Component({
  selector: 'sc-permissions-bulk-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MultiSelect, TranslatePipe],
  template: `
    <zag-multi-select
      [options]="permissionOptions()"
      [placeholder]="'bulkEdit.permissions.selectPermissions' | translate"
      [searchable]="true"
      [searchPlaceholder]="'bulkEdit.permissions.searchPermissions' | translate"
      [noResultsText]="'bulkEdit.permissions.noPermissions' | translate"
      [clearAriaLabel]="'bulkEdit.permissions.clearPermissions' | translate"
      [removeAriaLabel]="'bulkEdit.permissions.removePermission' | translate"
      [ngModel]="state().permissionIds"
      (ngModelChange)="onPermissionIdsChange($event)"
    />
  `,
})
export class PermissionsBulkSectionComponent implements OnInit, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly formService = inject(BulkEditSectionFormService);

  protected readonly state = signal<State>({ permissionIds: [] });

  private readonly permissionsResource = rxResource({
    stream: () => this.http.get<ManagedPermission[]>('/api/v1/rbac/permissions'),
  });

  protected readonly permissionOptions = computed<DropdownOption<string>[]>(() =>
    (this.permissionsResource.value() ?? []).map(permission => ({
      value: permission.id,
      label: permission.id,
    }))
  );

  protected onPermissionIdsChange(permissionIds: string[]): void {
    this.state.set({ permissionIds: [...permissionIds] });
  }

  ngOnInit(): void {
    this.formService.register(SECTION_ID, () => this.state());
  }

  ngOnDestroy(): void {
    this.formService.unregister(SECTION_ID);
  }
}
